import { createFileRoute, Link } from "@tanstack/react-router";
import { Sun, Clock, Umbrella, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/bay-beach-crystal-beach-guide")({
  head: () => ({
    meta: [
      { title: "Planning a Beach Day at Bay Beach - Malak BnB Resort" },
      { name: "description", content: "A guide to spending a day at Bay Beach in Crystal Beach, Ontario: when to go, what to bring, and staying five minutes away at Malak BnB Resort." },
      { property: "og:title", content: "Planning a Beach Day at Bay Beach - Malak BnB Resort" },
      { property: "og:description", content: "Everything to know before a day at Bay Beach in Crystal Beach, Ontario." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/bay-beach-crystal-beach-guide" },
      { property: "og:image", content: "/images/beach-bay.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Planning a Beach Day at Bay Beach", path: "/blog/bay-beach-crystal-beach-guide" },
        ]),
      },
    ],
    links: [canonical("/blog/bay-beach-crystal-beach-guide")],
  }),
  component: BayBeachGuidePage,
});

const FAQ = [
  {
    q: "How close is Malak BnB Resort to Bay Beach?",
    a: "Malak BnB Resort is a 5-minute walk from Bay Beach in Crystal Beach, Ontario. Guests can walk down for the morning and be back at the property by early afternoon.",
  },
  {
    q: "What is Bay Beach like?",
    a: "Bay Beach is a wide stretch of fine sand on the Canadian side of Lake Erie, known for calm, warm water in summer and a relaxed, family-friendly atmosphere. It gets busy on hot July and August weekends and stays easy the rest of the season.",
  },
  {
    q: "What is the best time of day to visit Bay Beach?",
    a: "Mornings before 11am are quieter and cooler, especially in July and August. The beach fills in through the afternoon on summer weekends, and evenings often bring a good sunset with fewer people around.",
  },
  {
    q: "Does Malak BnB Resort have a pool if Bay Beach is too crowded?",
    a: "Yes. Malak BnB Resort has an outdoor heated pool, a good alternative on busy beach days or when you simply want to stay closer to the room.",
  },
];

function BayBeachGuidePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/beach-bay.webp"
          alt="Bay Beach at Crystal Beach, Ontario on Lake Erie"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Bay Beach Guide</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Lake Erie, Ontario</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Planning a Beach Day at Bay Beach
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Bay Beach is the reason most people find Crystal Beach in the first place. It is a wide, easy stretch
              of sand on Lake Erie, the kind of beach that does not need much explaining once you are standing on
              it. Calm water in summer, a relaxed crowd, and enough space that it rarely feels as busy as it
              actually is.
            </p>
            <p>
              A little planning makes the day easier, especially in peak summer. Here is what to know before you go.
            </p>
          </div>
        </div>
      </section>

      {/* Timing */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Timing It Right</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                When to Go
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Bay Beach is quietest in the morning, especially before 11am on July and August weekends. The
                afternoon brings the biggest crowds, and evenings often bring a good sunset with the beach thinning
                out again. Weekday visits are noticeably calmer than weekends throughout the summer.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/explore-ridge-beach.webp" alt="Shoreline near Bay Beach at Crystal Beach, Ontario" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* What to bring */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <Umbrella className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">Making a Full Day of It</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
            What to Bring and Where to Retreat
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Shade is limited directly on the sand, so an umbrella or a beach tent is worth packing for a full day.
            Bring water and sunscreen, since the nearest shops are a short walk into town rather than right at the
            beach. If the crowd or the heat gets to be too much, Malak BnB Resort is five minutes away with an
            outdoor heated pool as a quieter alternative.
          </p>
        </div>
      </section>

      {/* Stay */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/gallery-pool-outdoor.webp" alt="Outdoor heated pool at Malak BnB Resort" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sun className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Where to Stay</p>
              </div>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                Five Minutes From the Sand
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Malak BnB Resort sits a five minute walk from Bay Beach, close enough to head down for the morning
                and be back for lunch. Continental breakfast is included every day, and the outdoor heated pool
                gives you a second option when the beach is at its busiest.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                  Request to Book
                </Link>
                <Link to="/rooms" className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  View Rooms and Rates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center pt-4 pb-16">
          <Link to="/summer-stays-crystal-beach" className="text-primary font-semibold text-sm hover:underline">
            Read more about summer stays at Crystal Beach →
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Planning Your Beach Day</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Stay five minutes from Bay Beach
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
